/***
*
*   CREDIT PREVIEW
*   Client-side prediction of what an Add Credits submission will do to an
*   account's credit position (CORE-3863). Mirrors LCWebApp
*   services/credit/adjust.ts + constants.ts: a positive workspace-pool grant
*   always settles any standing deficit first, the remainder lands as a fresh
*   lot expiring 3 months out (clamped to month-end), and FIFO consumption
*   order is landed_at ascending — so a new lot always lands behind whichever
*   active lot landed most recently. If this ever disagrees with the server,
*   this is the thing support trusts — keep it in this one tested function
*   rather than inlined in the modal.
*
**********/

const LOT_EXPIRY_MONTHS = 3;

// Adds N months to a date, clamping the day-of-month to the target month's
// last day when it doesn't exist there (e.g. Jan 31 + 3mo -> Apr 30).
export function addMonthsClamped(date, months){

  const d = new Date(date);
  const targetMonthIndex = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonthIndex / 12);
  const normalizedMonth = ((targetMonthIndex % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const day = Math.min(d.getDate(), lastDayOfTargetMonth);
  return new Date(targetYear, normalizedMonth, day, d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());

}

function fmt(n){ return n.toLocaleString(); }

// Formats a Date's LOCAL calendar date (not toISOString(), which reads UTC
// fields and would disagree with addMonthsClamped's local-field arithmetic
// by a day in any timezone ahead of UTC, e.g. Pacific/Auckland).
function dateStr(d){

  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;

}

export function computeAddCreditsPreview({
  pool, amount, accountDeficit = 0, activeLots = [],
  personalGranted = 0, personalRemaining = 0, personalPeriodEnd = null,
  now = new Date(),
}){

  if (pool === 'personal'){

    if (amount > 0){

      const lead = `Adds ${fmt(amount)} credits to this user's allowance for the current cycle`
        + (personalPeriodEnd ? `, expiring ${dateStr(personalPeriodEnd)}.` : '.');
      return {
        summary: `${lead} Spent before the workspace pool. Their plan and next refill are unchanged.`,
        deficitSettled: 0,
        newLotExpiresAt: null,
      };

    }

    return {
      summary: `Claws back ${fmt(Math.abs(amount))} credits from this user's allowance for the current cycle `
        + `(currently ${fmt(personalRemaining)} of ${fmt(personalGranted)}). Their plan and next refill are unchanged.`,
      deficitSettled: 0,
      newLotExpiresAt: null,
    };

  }

  // pool === 'account'
  if (amount < 0){

    return {
      summary: `Claws back ${fmt(Math.abs(amount))} credits from the workspace pool, oldest bundles first.`,
      deficitSettled: 0,
      newLotExpiresAt: null,
    };

  }

  const deficitSettled = Math.min(amount, accountDeficit);
  const remainder = amount - deficitSettled;
  const newLotExpiresAt = remainder > 0 ? addMonthsClamped(now, LOT_EXPIRY_MONTHS) : null;
  const byLandedAtAsc = [...activeLots].sort((a, b) => new Date(a.landedAt) - new Date(b.landedAt));
  const behindLot = byLandedAtAsc.length ? byLandedAtAsc[byLandedAtAsc.length - 1] : null;

  const sentences = [
    `Adds ${fmt(amount)} credits to the workspace pool` + (remainder > 0 ? `, expiring ${dateStr(newLotExpiresAt)}.` : '.'),
  ];

  if (deficitSettled > 0)
    sentences.push(`Settles the ${fmt(-accountDeficit)} negative balance first, leaving ${fmt(remainder)} spendable.`);

  if (remainder > 0 && behindLot)
    sentences.push(`Sits behind the ${dateStr(behindLot.landedAt)} bundle in FIFO order.`);

  return { summary: sentences.join(' '), deficitSettled, newLotExpiresAt };

}
